import React, {useEffect} from "react";
import useTranslate from "../../../../core/languages/hooks/useTranslate";
import {useFormContext, Controller} from "react-hook-form";
import EventCity from "../../../../eventCities/types/EventCity";
import {ArrivalTransportSelect, DepartureTransportSelect, TransportEnum} from "../../../../transfers/enums/TransferTransportEnum";
import {TransferDirectionEnum, TransferDirectionRadioInput} from "../../../../transfers/enums/TransferDirectionEnum";
import {handleRadioChange} from "../../../../transfers/utils/handleTransferDirection";
import TransportDate from "../../../../transfers/components/dateAndTimeComponents/TransportDate";
import TransportTime from "../../../../transfers/components/dateAndTimeComponents/TransportTime";
import NumberOfPassengersField from "../../../../transfers/components/passengerComponents/NumberOfPassengersField";
import ArrivalTransportCompanyAndNumber
  from "../../../../transfers/components/referenceComponents/ArrivalTransportCompanyAndNumber";
import ArrivalLocationFrom from "../../../../transfers/components/locationComponents/ArrivalLocationFrom";
import ArrivalLocationTo from "../../../../transfers/components/locationComponents/ArrivalLocationTo";
import DepartureTransportCompanyAndNumber
  from "../../../../transfers/components/referenceComponents/DepartureTransportCompanyAndNumber";
import DepartureLocationFrom from "../../../../transfers/components/locationComponents/DepartureLocationFrom";
import DepartureLocationTo from "../../../../transfers/components/locationComponents/DepartureLocationTo";
import {Alert} from "../../../../ui/shared/Alert/Alert";
import HotelDetail from "../../../../hotels/types/HotelDetail";

interface StepTransferDetailsProps {
  eventCity?: EventCity;
  hotelInfo?: HotelDetail;
}

const StepTransferDetails: React.FC<StepTransferDetailsProps> = ({eventCity, hotelInfo}) => {
  const t = useTranslate();
  const {control, watch, setValue, formState: {errors}} = useFormContext();

  const direction = watch("direction");
  const arrivalTransport = watch("arrivalTransport");
  const departureTransport = watch("departureTransport");

  // Clear reference fields when transport is OTHER
  useEffect(() => {
    if (arrivalTransport === TransportEnum.OTHER) {
      setValue("arrivalTransportReference", "");
      setValue("arrivalTransportReferenceNumber", "");
    }
  }, [arrivalTransport, setValue]);

  useEffect(() => {
    if (departureTransport === TransportEnum.OTHER) {
      setValue("departureTransportReference", "");
      setValue("departureTransportReferenceNumber", "");
    }
  }, [departureTransport, setValue]);

  return (
    <div className="space-y-6">
      {/* --- TRANSFER DETAILS HEADER --- */}
      <div className="pb-4 border-b border-neutral-200 dark:border-neutral-700">
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
          {t("transfers.fields.transferDetails")}
        </h3>
      </div>
      {/* Success Banner */}
      <Alert type="success" closable={false} className="mb-6 border-l-4 border-green-500">
        <span className="font-medium">
          {t("packages.message.packageIncludedOneTransfer")}
        </span>
      </Alert>
      {/* --- TYPE OF TRANSFER --- */}
      <div>
        <h4 className="text-base font-medium text-neutral-700 dark:text-neutral-300 mb-4">
          {t("transfers.message.typeOfTransfer")}
        </h4>
        <Controller
          name="direction"
          control={control}
          rules={{required: t("common.feedback.field_required")}}
          render={({field}) => (
            <TransferDirectionRadioInput
              value={field.value}
              onChange={(event) => handleRadioChange(event, field.value, setValue)}
              allowRoundTrip={false}
            />
          )}
        />
      </div>

      {direction === TransferDirectionEnum.ARRIVAL && (
        <div className="space-y-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
          <div className="pb-4">
            <h4 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
              {t("transfers.fields.oneWayTransfer")}
            </h4>
          </div>
          {/* ...existing code... */}
          <ArrivalTransportSelect control={control} errors={errors} t={t}/>
          {/* Transport Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <TransportDate name="arrivalTransportDate" transportFieldName="arrivalTransport" direction="arrival" eventCity={eventCity}/>
            <TransportTime name="arrivalTransportTime" transportFieldName="arrivalTransport" direction="arrival"  dateFieldName="arrivalTransportDate"/>
            <NumberOfPassengersField name="arrivalNumberOfPassengers" direction="arrival"/>
          </div>
          {/* Transport Reference Fields */}
          {Number(arrivalTransport) !== TransportEnum.OTHER && (
            <ArrivalTransportCompanyAndNumber/>
          )}
          {/* Location Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ArrivalLocationFrom/>
            <ArrivalLocationTo packageWizard={true} hotelInfo={hotelInfo}/>
          </div>
        </div>
      )}
      {direction === TransferDirectionEnum.DEPARTURE && (
        <div className="space-y-4 pt-4 border-t border-neutral-200 dark:border-neutral-700">
          <div className="pb-4">
            <h4 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
              {t("transfers.fields.returnTransfer")}
            </h4>
          </div>
          {/* Transport Type */}
          <DepartureTransportSelect control={control} errors={errors} t={t}/>
          {/* Transport Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <TransportDate name="departureTransportDate" transportFieldName="departureTransport" direction="departure"
                           eventCity={eventCity}/>
            <TransportTime name="departureTransportTime" transportFieldName="departureTransport" direction="departure" dateFieldName="departureTransportDate"/>
            <NumberOfPassengersField name="departureNumberOfPassengers" direction="departure"/>
          </div>
          {/* Transport Reference Fields */}
          {Number(departureTransport) !== TransportEnum.OTHER && (
            <DepartureTransportCompanyAndNumber/>
          )}
          {/* Location Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DepartureLocationFrom packageWizard={true} hotelInfo={hotelInfo}/>
            <DepartureLocationTo/>
          </div>
        </div>
      )}
      <div className="space-y-6 mt-8">
        {/* Comments */}
        <div>
          <Controller
            name="comments"
            control={control}
            render={({ field }) => (
              <textarea
                {...field}
                placeholder={t("transfers.fields.comments") || "Comments"}
                rows={4}
                className="w-full px-4 py-3 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            )}
          />
        </div>
      </div>
    </div>
  );
}

export default StepTransferDetails;

